// Module-level tie-breaker: a bare timestamp + fresh random bytes on every call (the
// naive approach) isn't sortable across two calls that land in the same millisecond,
// which happens almost every time at JS speeds — the trailing random bytes make
// ordering a coin flip. Cloudflare Workers also coarsen Date.now()/performance.now()
// for Spectre mitigation, so a sub-ms timer can't fill the gap. Instead: within the
// same millisecond, increment the previous random tail by 1 instead of drawing fresh
// bytes, which guarantees strictly increasing output.
let lastMs = -1;
let lastRand = 0n;

export function uuidv7(): string {
  const ms = Date.now();
  if (ms === lastMs) {
    // ponytail: carrying past the 80-bit random tail needs 2^80 calls in one
    // millisecond — not reachable, so this can't overflow into the timestamp bytes.
    lastRand += 1n;
  } else {
    lastMs = ms;
    const randBytes = new Uint8Array(10);
    crypto.getRandomValues(randBytes);
    lastRand = randBytes.reduce((acc, b) => (acc << 8n) | BigInt(b), 0n);
  }
  const bytes = new Uint8Array(16);
  const ts = BigInt(ms);
  for (let i = 0; i < 6; i++) bytes[i] = Number((ts >> BigInt(8 * (5 - i))) & 0xffn);
  for (let i = 0; i < 10; i++) bytes[6 + i] = Number((lastRand >> BigInt(8 * (9 - i))) & 0xffn);
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x70;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export const CODE_ALPHABET = '23456789ABCDEFGHJKMNPQRSTUVWXYZ';

export function credentialCode(year: number, random: () => number = Math.random): string {
  let s = '';
  for (let i = 0; i < 5; i++) {
    const idx = Math.min(CODE_ALPHABET.length - 1, Math.floor(random() * CODE_ALPHABET.length));
    s += CODE_ALPHABET[idx];
  }
  return `EA-${year}-${s}`;
}
